import { ApiHost } from '../config';

/**
 * WebSocket 客户端（浏览器原生实现）
 *
 * 与 wxapp websocket.js API 完全对齐，底层使用浏览器原生 WebSocket。
 * 连接生命周期：disconnected → connecting → connected → authenticated → ready
 */

const WS_HEARTBEAT_INTERVAL = 25000;
const WS_HEARTBEAT_TIMEOUT = 60000;
const WS_RECONNECT_BASE = 2000;
const WS_RECONNECT_MAX = 30000;

class WsClient {
  constructor() {
    this._ws = null;
    this._status = 'disconnected';
    this._listeners = {};
    this._statusListeners = [];
    this._responseHandlers = new Map();
    this._pendingQueue = [];

    this._subscriptions = { rooms: new Set(), patterns: new Set() };
    this._reconnectAttempts = 0;
    this._heartbeatTimer = null;
    this._authenticatePromise = null;
    this._setupPromise = null;
    this._manualClose = false;
    this._credentials = null;
    this._sessionRoom = null;
  }

  /**
   * 预存认证凭据，连接建立后自动发起认证
   * 应当在 connect() 之前调用。页面/组件层不应关心认证细节。
   */
  setCredentials(wsctoken, authorization) {
    this._credentials = { wsctoken, authorization };
    return this;
  }

  get status() {
    return this._status;
  }

  get isReady() {
    return this._status === 'ready';
  }

  get sessionRoom() {
    return this._sessionRoom;
  }

  onStatusChange(cb) {
    this._statusListeners.push(cb);
    return () => {
      this._statusListeners = this._statusListeners.filter((l) => l !== cb);
    };
  }

  _notifyStatus() {
    this._statusListeners.forEach((cb) => {
      try {
        cb(this._status);
      } catch (e) {
        console.error('[WsClient] Status listener error:', e);
      }
    });
  }

  on(type, cb) {
    if (!this._listeners[type]) {
      this._listeners[type] = [];
    }
    this._listeners[type].push(cb);
    return () => {
      this._listeners[type] = this._listeners[type].filter((l) => l !== cb);
    };
  }

  off(type, cb) {
    if (!this._listeners[type]) return;
    this._listeners[type] = this._listeners[type].filter((l) => l !== cb);
  }

  send(type, payload, options = {}) {
    const msg = {
      type,
      id: options.id || this._generateId(),
      ts: Date.now(),
      payload: payload || {},
    };

    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify(msg));
    } else {
      this._pendingQueue.push(msg);
    }

    return msg.id;
  }

  sendAndWait(type, payload, timeout = 10000) {
    return new Promise((resolve, reject) => {
      const msgId = this._generateId();

      const timer = setTimeout(() => {
        this._responseHandlers.delete(msgId);
        reject(new Error(`Response timeout for ${type} (${timeout}ms)`));
      }, timeout);

      this._responseHandlers.set(msgId, { resolve, reject, timer });

      const msg = {
        type,
        id: msgId,
        ts: Date.now(),
        payload: payload || {},
      };

      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify(msg));
      } else {
        this._pendingQueue.push(msg);
      }
    });
  }

  authenticate(wsctoken, authorization) {
    if (this._status === 'ready' || this._status === 'authenticated') {
      return Promise.resolve();
    }
    if (this._authenticatePromise) {
      return this._authenticatePromise;
    }

    this._authenticatePromise = this.sendAndWait('auth', {
      wsctoken,
      authorization,
    }).then((result) => {
      this._status = 'authenticated';
      if (result && result.__ws__ && result.__ws__.room_id) {
        this._sessionRoom = result.__ws__.room_id;
      }
      this._notifyStatus();
      return result;
    }).catch((err) => {
      this._authenticatePromise = null;
      throw err;
    });

    return this._authenticatePromise;
  }

  subscribe({ rooms = [], patterns = [], mode = 'union' } = {}) {
    return this.sendAndWait('subscribe', { rooms, patterns, mode });
  }

  unsubscribe({ rooms = [], patterns = [] } = {}) {
    return this.sendAndWait('unsubscribe', { rooms, patterns });
  }

  async setupSubscriptions({ rooms = [], patterns = [] } = {}) {
    if (this._setupPromise) return this._setupPromise;

    this._setupPromise = this.subscribe({ rooms, patterns, mode: 'replace' }).then((result) => {
      if (result && result.success !== false) {
        this._subscriptions.rooms = new Set(rooms);
        this._subscriptions.patterns = new Set(patterns);
        this._status = 'ready';
        this._notifyStatus();
        this._flushQueue();
      }
      return result;
    }).catch((err) => {
      this._setupPromise = null;
      throw err;
    });

    return this._setupPromise;
  }

  connect(url) {
    if (this._ws && (this._status === 'connected' || this._status === 'ready')) {
      return;
    }

    this._manualClose = false;
    this._status = 'connecting';
    this._notifyStatus();

    const wsUrl = url || ApiHost.replace(/^https?/, (match) => (match === 'https' ? 'wss' : 'ws')) + '/wss';

    try {
      this._ws = new WebSocket(wsUrl);

      this._ws.onopen = () => {
        this._status = 'connected';
        this._reconnectAttempts = 0;
        this._notifyStatus();
        this._startHeartbeat();

        // 有预存凭据则自动认证（全局层设定，页面层不关心）
        if (this._credentials) {
          this.authenticate(this._credentials.wsctoken, this._credentials.authorization)
            .then(() => {
              // 认证成功后清空待发队列（页面层的 subscribe 调用可能在认证前到达）
              this._flushQueue();
            })
            .catch((err) => {
              console.error('[WsClient] 自动认证失败:', err);
              this.close();
            });
        } else {
          // 无凭据时也尝试清空队列
          this._flushQueue();
        }
      };

      this._ws.onmessage = (event) => {
        this._onMessage(event.data);
      };

      this._ws.onclose = () => {
        this._cleanup();
        if (!this._manualClose) {
          this._scheduleReconnect(wsUrl);
        }
      };

      this._ws.onerror = (err) => {
        console.error('[WsClient] WebSocket error:', err);
      };
    } catch (err) {
      console.error('[WsClient] 连接失败:', err);
      this._scheduleReconnect(wsUrl);
    }
  }

  close() {
    this._manualClose = true;
    this._cleanup();
    if (this._ws) {
      this._ws.close();
      this._ws = null;
    }
    this._status = 'disconnected';
    this._authenticatePromise = null;
    this._setupPromise = null;
    this._notifyStatus();
  }

  // ─── 内部方法 ───

  _onMessage(data) {
    if (!data) return;

    try {
      if (data === 'pong') return;

      const msg = typeof data === 'string' ? JSON.parse(data) : data;

      if (msg.type === 'pong') return;

      if (msg.id && this._responseHandlers.has(msg.id)) {
        const handler = this._responseHandlers.get(msg.id);
        clearTimeout(handler.timer);
        this._responseHandlers.delete(msg.id);

        if (msg.error) {
          handler.reject(new Error(msg.error.message || JSON.stringify(msg.error)));
        } else {
          handler.resolve(msg.payload || msg);
        }
        return;
      }

      const handlers = this._listeners[msg.type];
      if (handlers) {
        handlers.forEach((cb) => {
          try {
            cb(msg.payload || msg);
          } catch (e) {
            console.error(`[WsClient] 消息处理错误 (${msg.type}):`, e);
          }
        });
      }
    } catch (e) {
      console.warn('[WsClient] 消息解析失败:', data, e);
    }
  }

  _flushQueue() {
    const queue = this._pendingQueue.slice();
    this._pendingQueue = [];
    queue.forEach((msg) => {
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify(msg));
      }
    });
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this._heartbeatTimer = setInterval(() => {
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, WS_HEARTBEAT_INTERVAL);
  }

  _stopHeartbeat() {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
  }

  _cleanup() {
    this._stopHeartbeat();
    this._ws = null;
    this._status = 'disconnected';
    this._authenticatePromise = null;
    this._setupPromise = null;
  }

  _scheduleReconnect(wsUrl) {
    const delay = Math.min(
      WS_RECONNECT_BASE * Math.pow(2, this._reconnectAttempts),
      WS_RECONNECT_MAX
    );
    this._reconnectAttempts += 1;

    setTimeout(() => {
      if (!this._manualClose) {
        this.connect(wsUrl);
      }
    }, delay);
  }

  _generateId() {
    return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }
}

const wsClient = new WsClient();

export default wsClient;
